import * as React from 'react';

import { scrollViewportBounds } from './scroll-viewport-bounds.js';

export type WheelStep = -1 | 1;

const LINE_HEIGHT_PX = 16;

function wheelPixels(event: WheelEvent, element: HTMLElement): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * LINE_HEIGHT_PX;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * element.clientHeight;
  return event.deltaY;
}

function edgeHiddenInScrollDirection(element: HTMLElement, deltaY: number): boolean {
  const rect = element.getBoundingClientRect();
  const viewport = scrollViewportBounds(element);
  return deltaY > 0 ? rect.bottom > viewport.bottom + 1 : rect.top < viewport.top - 1;
}

/** Turns each vertical wheel gesture over the element into one step, once the element is fully scrolled into view; passing no `onStep` leaves scrolling alone. */
export function useWheelStep<TElement extends HTMLElement>(
  onStep: ((step: WheelStep) => void) | undefined,
  { threshold = 60, idleMs = 250 }: { threshold?: number; idleMs?: number } = {},
) {
  const ref = React.useRef<TElement>(null);
  const onStepRef = React.useRef(onStep);
  React.useLayoutEffect(() => {
    onStepRef.current = onStep;
  });
  const enabled = onStep !== undefined;

  React.useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    let accumulated = 0;
    let stepped = false;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const scrollsPage = edgeHiddenInScrollDirection(element, event.deltaY);
      if (!scrollsPage) event.preventDefault();
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        accumulated = 0;
        stepped = false;
      }, idleMs);
      if (scrollsPage) stepped = true;
      if (stepped) return;
      accumulated += wheelPixels(event, element);
      if (Math.abs(accumulated) < threshold) return;
      stepped = true;
      onStepRef.current?.(accumulated > 0 ? 1 : -1);
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', handleWheel);
      clearTimeout(idleTimer);
    };
  }, [enabled, threshold, idleMs]);

  return ref;
}
